import { AccountPlanSection } from './AccountPlanSection';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { storeOrganizerAccess } from '@src/access/organizerAccess';
import {
  BoxShell,
  Button,
  SettingsActionRow,
  CheckIcon,
  Cluster,
  Input,
  KeyIcon,
  LinkIcon,
  Note,
  Panel,
  ParticipantFace,
  SettingsPanel,
  SettingsSection,
  SettingsSteps,
  Stack,
  Typography,
  UserIcon,
  showDialog,
} from '@src/components';
import { GuestEmailLink } from './GuestEmailLink';
import { getProfileName } from '@src/components/AccountSwitcher/profiles';
import { resolveApiErrorKey } from '@src/i18n/apiError';

import { boxPath } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
import { useAppDispatch, useAppSelector } from '@src/state/hooks';
import { loadSession } from '@src/state/sessionSlice';

function boxKeyFromRedirect(redirect?: string): string | undefined {
  if (!redirect) {
    return undefined;
  }
  try {
    const path = redirect.startsWith('http') ? new URL(redirect).pathname : redirect;
    const match = path.match(/^\/box\/([^/]+)/);
    return match?.[1];
  } catch {
    return undefined;
  }
}

export function GuestAccountPage() {
  useEffect(() => {
    if (window.location.hash === '#permanent-account')
      document.getElementById('permanent-account')?.scrollIntoView({ block: 'start' });
  }, []);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const [busy, setBusy] = useState(false);
  const [codesError, setCodesError] = useState(false);
  const creatingCodes = useRef(false);
  const [rename, setRename] = useState<{ name: string; error: string | null } | null>(null);
  const profiles = user?.profiles || [];
  const profile =
    profiles.find((item) => item.id === user?.id) ||
    profiles.find((item) => item.active) ||
    profiles[0];
  const featuredBox = profile?.boxes?.[0];
  const name = profile ? getProfileName(profile, t('switcher.profile')) : t('switcher.profile');
  const canRename = Boolean(featuredBox?.key && featuredBox.username && !featuredBox.isOrganizer);

  const renameGuest = useCallback(
    async (state: { name: string; error: string | null }) => {
      if (!featuredBox?.key) {
        return;
      }
      const username = state.name.trim();
      if (username === featuredBox.username) {
        setRename(null);
        return;
      }
      const result = await santaClient.saveCard({
        key: featuredBox.key,
        username,
      });
      if (!result.ok) {
        setRename({ ...state, error: result.error?.message ?? 'errors.UNKNOWN' });
        throw new Error('rename failed');
      }
      setRename(null);
      await dispatch(loadSession());
    },
    [dispatch, featuredBox],
  );

  useEffect(() => {
    if (!rename) {
      return;
    }
    showDialog({
      title: t('box.organizer.renameParticipant.title'),
      children: (
        <Input
          value={rename.name}
          onValueChange={(next) =>
            setRename((current) => (current ? { ...current, name: next, error: null } : null))
          }
          label={t('box.organizer.renameParticipant.label')}
          error={rename.error ? t(resolveApiErrorKey(rename.error)) : undefined}
          testId="guest-account-rename-input"
        />
      ),
      cancel: t('box.cancel'),
      submit: t('box.save'),
      submitDisabled: rename.name.trim().length < 2,
      onSubmit: () => renameGuest(rename),
      onCancel: () => setRename(null),
      testId: 'guest-account-rename-dialog',
    });
  }, [rename, renameGuest, t]);

  async function createCodes() {
    if (creatingCodes.current || busy) {
      return;
    }
    creatingCodes.current = true;
    setBusy(true);
    setCodesError(false);
    try {
      const res = await santaClient.rotateRecoveryCodes({});
      const data = res.data;
      const boxKey = featuredBox?.key || boxKeyFromRedirect(data?.redirect);
      if (res.ok && data?.recoveryCodes?.length && boxKey) {
        storeOrganizerAccess(boxKey, data, { rotatedFromSettings: true });
        navigate(boxPath(boxKey));
        return;
      }
    } catch {
      // One generic error.
    }
    creatingCodes.current = false;
    setCodesError(true);
    setBusy(false);
  }

  const avatar = <ParticipantFace card={profile} index={profile?.id ?? user?.id} size="row" />;

  return (
    <BoxShell width="medium" pad="compact" testId="guest-account">
      <Stack gap={8} wide className="settings">
        <Stack gap={3} as="header" wide className="landscape-sky-content">
          <PageHeading title={<>{t('account.guestTitle')}</>} subtitle={<>{t('account.guestHeroLead')}</>} />
        </Stack>

        <SettingsPanel>
          <AccountPlanSection />
          <SettingsSection
            title={t('account.guestProfile')}
            hint={t('account.guestNotice')}
            icon={<UserIcon />}
            iconTone="accent"
            testId="guest-account-profile"
          >
            <Panel padding="medium" testId="guest-account-identity">
              <Cluster align="center" gap={4} justify="between" wrap={false} wide>
                <Cluster align="center" gap={4} wrap={false}>
                  {avatar}
                  <Typography as="p" role="title/section">
                    {name}
                  </Typography>
                </Cluster>
                {canRename ? (
                  <Button
                    variant="soft"
                    size="small"
                    onClick={() => setRename({ name: featuredBox!.username || '', error: null })}
                    testId="guest-account-rename"
                  >
                    {t('box.organizer.menu.rename')}
                  </Button>
                ) : null}
              </Cluster>
            </Panel>
          </SettingsSection>

          <SettingsSection
            id="permanent-account"
            title={t('account.guestConvertTitle')}
            hint={t('account.guestConvertIntro')}
            icon={<LinkIcon />}
            iconTone="support"
          >
            <Stack gap={4}>
              <GuestEmailLink />
              <Typography as="h3" role="title/card">
                {t('account.guestExistingAccount')}
              </Typography>
              <SettingsSteps
                steps={[
                  t('account.guestConvertStep1'),
                  t('account.guestConvertStep2'),
                  t('account.guestConvertStep3'),
                ]}
                testId="guest-convert-steps"
              />
              <Note tone="success" icon={<CheckIcon />}>
                {t('account.guestConvertFooter')}
              </Note>
            </Stack>
          </SettingsSection>

          <SettingsSection
            title={t('account.guestCodesTitle')}
            hint={t('account.guestCodesMessage')}
            icon={<KeyIcon />}
            iconTone="neutral"
            testId="guest-account-codes-section"
          >
            <Stack gap={3}>
              <SettingsActionRow
                title={t('account.guestCodesCalloutTitle')}
                hint={t('account.guestCodesCalloutHint')}
                testId="guest-account-codes-card"
              >
                <Button
                  variant="secondary"
                  loading={busy}
                  onClick={() => {
                    void createCodes();
                  }}
                  testId="guest-account-codes"
                >
                  {t('account.guestCodesButton')}
                </Button>
              </SettingsActionRow>
              {codesError ? (
                <Typography as="p" role="meta/error">
                  {t('account.guestCodesError')}
                </Typography>
              ) : null}
            </Stack>
          </SettingsSection>
        </SettingsPanel>
      </Stack>
    </BoxShell>
  );
}
